import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Card, Loader, Stack, TagsInput, Text, Title } from "@mantine/core";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import z from "zod";

import { useSelectedCompany } from "@/features/dashboard/configuracoes/_context/SelectedCompanyContext";
import { useGetSettingsRecipientsByCompanyQuery, usePatchSettingsRecipientsByCompanyMutation, usePostSettingsRecipientsByCompanyMutation } from "@/store";

export const Route = createFileRoute("/_private/dashboard/_settings/configuracoes/dedo-duro")({
  component: RouteComponent
});

type FormValues = z.infer<typeof schema>;
const schema = z.object({
  // emails: z.array(z.email("Informe um e-mail válido")).min(1, "Informe pelo menos um e-mail.")
  emails: z.array(z.email("Informe um e-mail válido"))
});

function getArrayFieldErrors(error: unknown): Array<{ index: number; message: string }> {
  if (!Array.isArray(error)) return [];

  return error.flatMap((item, index) => {
    if (item && typeof item === "object" && "message" in item && typeof item.message === "string") {
      return [{ index, message: item.message }];
    }
    return [];
  });
}

function RouteComponent() {
  const { companyId } = useSelectedCompany();
  const { data, isLoading, isFetching } = useGetSettingsRecipientsByCompanyQuery({ id: +companyId! }, { skip: !companyId });
  const [postSettingsRecipients, { isLoading: isCreating }] = usePostSettingsRecipientsByCompanyMutation();
  const [patchSettingsRecipients, { isLoading: isSaving }] = usePatchSettingsRecipientsByCompanyMutation();

  const { handleSubmit, formState, control, reset } = useForm<FormValues>({
    mode: "onBlur",
    resolver: zodResolver(schema),
    defaultValues: {
      emails: []
    }
  });

  useEffect(() => {
    if (data) reset(data);
  }, [data, reset]);

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    if (!companyId) return;
    const args = { id: +companyId, ...values };
    if (data?.emails.length) await patchSettingsRecipients(args).unwrap();
    else await postSettingsRecipients(args).unwrap();
  };

  return (
    <Stack>
      <Stack gap={0}>
        <Title order={1}>Dedo Duro</Title>
        <Text>Gerenciamento dos destinatários de alertas de faltosos e tempo excedido</Text>
      </Stack>

      {isLoading && <Loader className="self-center" />}
      {!isLoading && data && (
        <Card component="form" onSubmit={handleSubmit(onSubmit)} shadow="sm" withBorder>
          <Stack gap="xs">
            <Stack gap={0}>
              <Title order={2}>Destinatários dos alertas</Title>
              <Text c="dimmed" size="sm">
                A mesma lista recebe notificações de motorista faltoso e relatórios de veículos com tempo excedido.
              </Text>
            </Stack>

            <Controller
              name="emails"
              control={control}
              render={({ field, formState: { errors } }) => {
                const emailErrors = getArrayFieldErrors(errors.emails);
                const listError = errors.emails && !Array.isArray(errors.emails) ? errors.emails.message : undefined;

                return (
                  <Stack gap={4}>
                    <TagsInput
                      {...field}
                      label="E-mails"
                      placeholder="Digite um e-mail e pressione Enter"
                      splitChars={[",", ";"]}
                      error={emailErrors.length > 0 || Boolean(listError)}
                      clearable
                    />
                    {listError && (
                      <Text c="red" size="xs">
                        {listError}
                      </Text>
                    )}
                    {emailErrors.map(({ index, message }) => (
                      <Text key={index} c="red" size="xs">
                        {field.value[index]}: {message}
                      </Text>
                    ))}
                  </Stack>
                );
              }}
            />

            <Button type="submit" loading={isCreating || isSaving} disabled={!formState.isDirty || isFetching} className="self-end max-md:w-full!">
              Salvar destinatários
            </Button>
          </Stack>
        </Card>
      )}
    </Stack>
  );
}
